import { IComponentOptions, IController, IQService, ITimeoutService, IScope, IPromise } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { Unsubscribe } from "redux";

import { IGtmhubRootScopeService } from "@gtmhub/models";
import { UserService } from "@gtmhub/users/userService";
import { GoalService } from "@gtmhub/goals/goalService";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { OnboardingChecklistModalCtrl, OnboardingService } from "@gtmhub/onboarding";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { INgRedux } from "@gtmhub/state-management";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { getCurrentUserId, hasCurrentUserRole } from "@gtmhub/users";

const bindStateToScope = (state: ISessionsStoreState): Record<string, unknown> => {
  const activeSessions = state.sessions.items;

  return {
    activeSessions,
  };
};

interface IOnboardingChecklistBindings {
  hasAccountEditionPermission: boolean;
}

export interface IChecklist {
  stepsV1: {
    watchedVideo: boolean;
    invitedTeammate: boolean;
    createdOkr: boolean;
    visitedDemoAccount: boolean;
    visitedMarketplace: boolean;
  };
  stepsV1Completed: boolean;
  showCards: {
    inviteTeammate: boolean;
    visitDemoAccount: boolean;
    visitMarketplace: boolean;
  };
  optOut: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface OnboardingChecklistCtrl extends IOnboardingChecklistBindings {}
export class OnboardingChecklistCtrl implements IController {
  // view variables
  activeSessions: IPlanningSession[];
  progress: number;
  percent: number;
  availableActions: number;
  checklist: IChecklist;

  // private variables
  sessionsIds: string[];
  isAdmin: boolean;
  unsubscribeFromStore: Unsubscribe;
  private unsubscribeFromActiveSessions: () => void;

  static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "$uibModal",
    "$timeout",
    "$ngRedux",
    "AnalyticsService",
    "UserService",
    "GoalService",
    "CurrentUserService",
    "PlanningSessionsActions",
    "OnboardingService",
  ];

  constructor(
    private $scope: IScope,
    private $rootScope: IGtmhubRootScopeService,
    private $q: IQService,
    private $uibModal: IModalService,
    private $timeout: ITimeoutService,
    private $ngRedux: INgRedux,
    private analyticsService: AnalyticsService,
    private userService: UserService,
    private goalService: GoalService,
    private currentUserService: CurrentUserService,
    private planningSessionsActions: PlanningSessionsActions,
    private onboardingService: OnboardingService
  ) {
    this.unsubscribeFromActiveSessions = this.$scope.$watch("$ctrl.activeSessions", (newValue: IPlanningSession[]) => {
      if (newValue) {
        this.filterSessions();
        this.checkForAvailableActions();
        this.checkForTeammatesAndOkrs();
      }
    });
  }

  $onInit(): void {
    this.checklist = this.onboardingService.initChecklist();

    const removeUsersChangedListener = this.$rootScope.$on("usersChanged", () => {
      this.checkForTeammatesAndOkrs();
    });
    const removeGoalsChangedListener = this.$rootScope.$on("goalsChanged", () => {
      this.checklist.stepsV1.createdOkr = true;
      this.loadProgress();
    });

    const removeOnboardingChecklistUpdatedListener = this.$rootScope.$on("onboarding.checklistUpdated", (evt, checklist: IChecklist) =>
      this.onOnboardingChecklistUpdated(checklist)
    );

    this.$scope.$on("$destroy", () => {
      removeUsersChangedListener();
      removeGoalsChangedListener();
      removeOnboardingChecklistUpdatedListener();
    });

    this.unsubscribeFromStore = this.$ngRedux.connect(bindStateToScope.bind(this))(this);
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    this.filterSessions();
    this.checkForAvailableActions();
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
    this.unsubscribeFromActiveSessions();
  }

  openChecklist(): void {
    this.analyticsService.setOnboardingProperties(this.checklist);

    this.$uibModal
      .open({
        templateUrl: require("../views/checklist.jade"),
        windowClass: "medium-small checklist-modal",
        backdropClass: "dark-backdrop",
        backdrop: false,
        controller: OnboardingChecklistModalCtrl,
        resolve: {
          checklist: () => {
            return this.checklist;
          },
          progress: () => {
            return this.progress;
          },
          availableActions: () => {
            return this.availableActions;
          },
          isAdmin: () => {
            return this.isAdmin;
          },
          hasPermissionsToAccessPlanUpdate: () => {
            return this.hasAccountEditionPermission;
          },
        },
      })
      .result.then((checklist) => {
        if (checklist) {
          this.checklist = checklist;
          this.loadProgress();
        }
      });
  }

  private filterSessions = (): void => {
    this.sessionsIds = this.activeSessions.reduce((sessions, session) => {
      // use object.keys
      sessions.push(session.id);

      return sessions;
    }, []);
  };

  private checkForAvailableActions = (): void => {
    this.availableActions = 3;
    if (this.$rootScope.hasPermission("ManageUsers")) {
      this.availableActions++;
    }

    if (this.$rootScope.hasPermission("ManageGoals") && this.sessionsIds.length > 0) {
      this.availableActions++;
    }

    this.isAdmin = hasCurrentUserRole("admin");
    if (this.isAdmin && this.hasAccountEditionPermission) {
      this.availableActions++;
    }

    this.loadProgress();
  };

  private checkForTeammatesAndOkrs = (): void => {
    const promises: { [id: string]: IPromise<unknown> } = {};

    if (!this.checklist.stepsV1.invitedTeammate && this.$rootScope.hasPermission("ManageUsers")) {
      promises.teammates = this.userService.getUsers({ skip: 0, take: 1, includeShadowedRoles: true });
    }

    if (!this.checklist.stepsV1.createdOkr && this.$rootScope.hasPermission("ManageGoals")) {
      this.sessionsIds.forEach((s) => {
        promises[s] = this.goalService.getGoalsV2({
          filter: { sessionId: { $in: [s] } },
          fields: "id,createdById",
        });
      });
    }

    this.$q
      .all(promises)
      .then((items: any) => {
        const currentUserId = getCurrentUserId();

        this.sessionsIds.forEach((s) => {
          if (items[s] && items[s].totalCount) {
            const thisUserHasCreatedOkr = items[s].items.some((goal) => goal.createdById === currentUserId);
            this.checklist.stepsV1.createdOkr = thisUserHasCreatedOkr;
          }
        });

        if (items.teammates) {
          // should be changed in the backend to return createdById
          this.checklist.stepsV1.invitedTeammate = items.teammates.totalCount > 1;
        }
      })
      // since this directive initiates with when the leftside navigation is initialized
      // we don't want if there is an error to throw it because the person may not at all want to open
      // this directive - instead silently load the progress
      .finally(() => {
        this.loadProgress();
      });
  };

  private loadProgress = (): void => {
    this.progress = this.getProgress(this.checklist);
    this.percent = (100 / this.availableActions) * this.progress;

    if (this.percent === 100) {
      this.checklist.stepsV1Completed = true;
      this.analyticsService.track("Onboarding Checklist Completed");
    }
    this.currentUserService.setUserSetting({ checklist: this.checklist });

    this.$timeout(() => {
      const barEl = document.getElementById("left-circular-progress-bar");
      if (barEl) {
        barEl.style.transform = `rotate(${this.percent * 3.6}deg)`;
      }
    });
  };

  private getProgress = (list: IChecklist): number => {
    return Object.keys(list.stepsV1).reduce((acc, item) => {
      if (list.stepsV1[item]) {
        acc++;
      }
      return acc;
    }, 0);
  };

  private onOnboardingChecklistUpdated(checklist: IChecklist): void {
    this.checklist = Object.assign({}, checklist);
    this.loadProgress();
  }
}

export const OnboardingChecklistComponent: IComponentOptions = {
  templateUrl: require("./onboarding-checklist.jade"),
  controller: OnboardingChecklistCtrl,
  bindings: {
    hasAccountEditionPermission: "<",
  },
};
